// API Konfigürasyonu - Tüm API URL'leri burada merkezi olarak yönetilir

// API Base URL - Runtime'da otomatik çözülür veya .env'den okunur
// Bu sayede tek bir build tüm domain'lerde çalışır
const getApiBaseUrl = () => {
  // Önce .env'den kontrol et
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Production: window.location.origin + '/api'
  if (typeof window !== 'undefined') {
    return window.location.origin + '/api';
  }
  
  return 'https://example.com/api';
};

export const API_BASE_URL = getApiBaseUrl();

// Domain'i API Base URL'den çıkar (https://example.com/api -> https://example.com)
export const getDomain = () => {
  const apiUrl = API_BASE_URL;
  // https://example.com/api -> https://example.com
  const match = apiUrl.match(/^(https?:\/\/[^/]+)/);
  return match ? match[1] : 'https://example.com';
};

// Domain'i export et
export const DOMAIN = getDomain();

// API endpoint'leri
export const API_ENDPOINTS = {
  // Authentication
  LOGIN: '/login.php',
  LOGOUT: '/logout.php',
  REGISTER: '/register.php',
  CHECK_AUTH: '/check-auth.php',
  CHECK_ADMIN: '/check-admin.php',
  FORGOT_PASSWORD: '/forgot-password.php',
  RESET_PASSWORD: '/reset-password.php',

  // Content Management
  GET_CONTENT: '/get-content.php',
  CONTENT: '/content.php',
  CHECK_CONTENT: '/check-content.php',
  UPDATE_CONTENT: '/update-content.php',
  DELETE_CONTENT: '/delete-content.php',

  // Episodes & Seasons
  GET_EPISODES: '/get-episodes.php',
  GET_SEASONS: '/get-seasons.php',
  ADD_EPISODE: '/add-episode.php',
  ADD_SEASON: '/add-season.php',
  DELETE_EPISODE: '/delete-episode.php',
  DELETE_SEASON: '/delete-season.php',
  MERGE_SEASONS: '/merge-seasons.php',

  // TMDB Proxy
  FETCH_DETAILS: '/fetch-details.php',
  FETCH_SEASON: '/fetch-season.php',
  FETCH_KEYWORDS: '/fetch-keywords.php',
  TMDB_SEARCH: '/tmdb-search.php',
  TMDB_PROXY: '/tmdb-proxy.php',

  // User Management
  GET_USERS: '/get-users.php',
  DELETE_USER: '/delete-user.php',
  UPDATE_USER_ROLE: '/update-user-role.php',
  GET_USER_PROFILE: '/get-user-profile.php',
  UPDATE_PROFILE: '/update-profile.php',

  // Categories & Titles
  GET_CATEGORIES: '/get-categories.php',
  ADD_CATEGORY: '/add-category.php',
  DELETE_CATEGORY: '/delete-category.php',
  GET_ALTERNATE_TITLES: '/get-alternate-titles.php',
  GET_ALL_ALTERNATE_TITLES: '/get-all-alternate-titles.php',
  ADD_ALTERNATE_TITLE: '/add-alternate-title.php',
  DELETE_ALTERNATE_TITLE: '/delete-alternate-title.php',

  // User Actions
  TOGGLE_FAVORITE: '/toggle-favorite.php',
  CHECK_FAVORITE: '/check-favorite.php',
  GET_USER_FAVORITES: '/get-user-favorites.php',
  GET_PUBLIC_FAVORITES: '/get-public-favorites.php',
  TOGGLE_WATCHLIST: '/toggle-watchlist.php',
  CHECK_WATCHLIST: '/check-watchlist.php',
  GET_USER_WATCHLIST: '/get-user-watchlist.php',

  // Comments
  COMMENTS: '/comments.php',
  DELETE_COMMENTS: '/delete-comments.php',
  GET_ALL_COMMENTS: '/get-all-comments.php',
  UPDATE_COMMENT: '/update-comment.php',

  // Settings
  GET_SETTINGS: '/get-settings.php', // Admin only - tüm ayarlar
  GET_PUBLIC_SETTINGS: '/get-public-settings.php', // Public - sadece public ayarlar
  SAVE_SETTINGS: '/save-settings.php',
  HOME_SETTINGS: '/home-settings.php',

  // License
  CHECK_LICENSE: '/check-license.php',

  // Images
  PROXY_IMAGE: '/proxy-image.php',
  UPLOAD_IMAGE: '/upload-image.php',
  GET_MEDIA_LIBRARY: '/get-media-library.php',
  UPLOAD_MEDIA: '/upload-media.php',
  DELETE_MEDIA: '/delete-media.php',
  RENAME_MEDIA: '/rename-media.php',

  // Database
  LIST_TABLES: '/list-tables.php',
  TEST_DATABASE: '/test-database.php',

  // Dynamic Pages
  GET_PAGES: '/get-pages.php',
  SAVE_PAGES: '/save-page.php',

  // Calendar
  GET_CALENDAR_EPISODES: '/get-calendar-episodes.php',

  // Security
  GET_CSRF_TOKEN: '/get-csrf-token.php'
};

// Tam URL oluşturmak için helper fonksiyon
export const getApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};

// Debug için console'a yazdır (sadece development'ta)
// getApiUrl tanımlandıktan sonra çağrılmalı
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  console.log('API Base URL:', API_BASE_URL);
  console.log('Full API URL example:', getApiUrl('/get-public-settings.php'));
}

// Fetch wrapper fonksiyonu - hata yönetimi ve CSRF token ile
export const apiRequest = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  
  // CSRF token'ı dinamik olarak import et (circular dependency önlemek için)
  const { getCsrfToken, getCachedCsrfToken } = await import('../utils/csrfToken');
  
  const method = (options.method || 'GET').toUpperCase();
  const needsCsrfToken = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
  
  // CSRF token ekle (POST/PUT/DELETE için)
  let csrfToken = null;
  if (needsCsrfToken) {
    csrfToken = await getCsrfToken();
  }
  
  const defaultOptions = {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
      ...(options.headers || {})
    },
    credentials: 'include',
    ...options
  };

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

// JSON response için wrapper
export const apiRequestJson = async (endpoint, options = {}) => {
  const response = await apiRequest(endpoint, options);
  return response.json();
};
